import React from 'react';
import { Order } from '../../../types';
import { useNavigate } from 'react-router-dom';

interface OrderCardProps {
  order: Order;
  onActionClick: (newStatus: string) => void;
}

const allowedTransitions: { [key: string]: string[] } = {
  pending: ['Confirm Order', 'Mark as Processing', 'cancelled'],
  confirmed: ['Mark as Processing', 'Mark as Shipped', 'cancelled'],
  processing: ['Mark as Shipped', 'cancelled'],
  shipped: ['Mark as Delivered'],
  delivered: [],
  cancelled: [],
};

const getStatusStyles = (status: string) => {
  switch (status.toLowerCase()) {
    case 'pending':
      return { border: 'border-yellow-500', bg: 'bg-yellow-100', text: 'text-yellow-800' };
    case 'confirmed':
      return { border: 'border-blue-500', bg: 'bg-blue-100', text: 'text-blue-800' };
    case 'processing':
      return { border: 'border-purple-500', bg: 'bg-purple-100', text: 'text-purple-800' };
    case 'shipped':
      return { border: 'border-cyan-500', bg: 'bg-cyan-100', text: 'text-cyan-800' };
    case 'delivered':
      return { border: 'border-green-500', bg: 'bg-green-100', text: 'text-green-800' };
    case 'cancelled':
      return { border: 'border-red-500', bg: 'bg-red-100', text: 'text-red-800' };
    default:
      return { border: 'border-gray-300', bg: 'bg-gray-100', text: 'text-gray-600' };
  }
};

const getButtonStyle = (status: string) => {
  switch (status) {
    case 'confirmed':
      return 'bg-blue-500 hover:bg-blue-600';
    case 'processing':
      return 'bg-purple-500 hover:bg-purple-600';
    case 'shipped':
      return 'bg-cyan-500 hover:bg-cyan-600';
    case 'delivered':
      return 'bg-green-500 hover:bg-green-600';
    case 'cancelled':
      return 'bg-red-500 hover:bg-red-600';
    default:
      return 'bg-gray-500 hover:bg-gray-600';
  }
};

const OrderCard: React.FC<OrderCardProps> = ({ order, onActionClick }) => {
  const navigate = useNavigate();
  const statusStyles = getStatusStyles(order.status.toLowerCase());
  const nextStatuses = allowedTransitions[order.status.toLowerCase()] || [];

  return (
    <div className={`border ${statusStyles.border} rounded-lg p-4`} role="region" aria-labelledby={`order-${order.id}`}>
      <div className="flex justify-between items-start">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-neutral-900">Order #{order.id}</h3>
          <p className="text-sm text-neutral-600">Placed on: {new Date(order.created_at).toLocaleDateString()}</p>
          <p className="text-sm text-neutral-600">Total: ${order.total}</p>
          <p className={`text-sm font-medium ${statusStyles.text}`}>Status: {order.status}</p>

          {/* Shipping Preview */}
          <div className="mt-2 text-sm text-neutral-700 space-y-1">
            {order.address?.center && (
              <p>
                <span className="font-medium">Shipping:</span> {order.payment_method}
              </p>
            )}
            {order.address?.compound && (
              <p>
                <span className="font-medium">Mobile:</span> {order.address.mobile}
              </p>
            )}
          </div>
        </div>

        <div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyles.bg} ${statusStyles.text}`}>
            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </span>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => navigate(`/admin/orders/${order.id}`)}
          className="bg-gray-100 text-gray-800 px-3 py-1 rounded text-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          View Details
        </button>

        {nextStatuses.map((status) => (
          <button
            key={status}
            onClick={() => onActionClick(status)}
            className={`text-white px-3 py-1 rounded text-sm ${getButtonStyle(status)} focus:outline-none focus:ring-2 focus:ring-offset-2`}
          >
            Mark as {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default OrderCard;
